<template>
    <el-card>
        <!-- Start loader -->
        <div class="text-center mt-2"
             v-if="isLoading">
            <h5>
                <i class="fa fa-spinner fa-spin"></i>
                <span>Loading...</span>
            </h5>
        </div>
        <!-- End loader -->
        <div v-else>
            <el-form :name="`${integration}-property-mapping`"
                     class="text-left"
                     @submit.native.prevent>
                <!-- Start Default Fields -->
                <div class="d-flex justify-content-between">
                    <h5>Default Fields</h5>
                </div>

              <div class="row form-group mt-3">
              <!-- Start Aloware and HubSpot Labels -->
                <div class="col-md-4 mb-1">
                  <small class="font-weight-bold">Aloware</small>
                </div>
                <div class="col-md-2 mb-1 text-center">
                  <small class="font-weight-bold">Direction</small>
                </div>
                <div class="col-md-4 mb-1">
                  <small class="font-weight-bold">{{ getIntegrationTitleName() }}</small>
                </div>
              <!-- End Aloware and HubSpot Labels -->
              </div>

                <div v-for="(prop,index) in defaultPropertyMappings"
                     :key="`${index}-default`"
                >
                  <div class="row form-group mt-3" v-if="isCorrectModule(prop.source_module)">
                    <!-- Start Aloware Fields -->
                    <div class="col-md-4">
                      <el-form-item class="mb-1">
                        <input type="hidden" v-model="prop.source_module" />
                        <el-select v-model="prop.aloware_key"
                                   class="w-full"
                                   filterable
                                   :disabled="!prop.is_mappable || alowareKeySelectionIsLocked(prop.aloware_key)"
                                   @change="handleSelectionChange(index, prop, 'aloware', 'default')">
                          <el-option
                              v-for="option in alowareDefaultOptions"
                              :key="option['key']"
                              :label="option['label']"
                              :disabled="!option['is_mappable'] || isSelectedOption(option['key'], prop.aloware_key)"
                              :value="option['key']">
                            <i v-if="isSelectedOption(option['key'], prop.aloware_key)"
                               class="fa fa-ban text-grey-5">
                            </i>
                            {{ option.direction === IntegrationPropertyMapDirections.DIRECTION_OUTBOUND ? `${option.label} (outbound-only)` : `${option.label}` }}
                          </el-option>
                        </el-select>
                      </el-form-item>
                    </div>
                    <!-- End Aloware Fields -->

                    <div class="col-md-2 text-center">
                      <template v-if="!hasAllowedOpenCustomDirections(prop.aloware_key)">
                        <direction-selector :initial-state="prop.direction"
                                            :disabled="shouldDisableDirection(index, prop, 'default')"
                                            @change="direction => updateDefaultDirection(index, direction)">
                        </direction-selector>
                      </template>
                      <template v-else>
                        <direction-selector :initial-state="prop.direction"
                                            :allowed-states="allowedOpenCustomDirections(prop.aloware_key)"
                                            @change="direction => updateDefaultDirection(index, direction)">
                        </direction-selector>
                      </template>
                    </div>

                    <!-- Start Integration Fields -->
                    <div class="col-md-4">
                      <el-form-item class="mb-1">
                        <el-select v-model="prop.external_key"
                                   class="w-full"
                                   filterable
                                   :disabled="!prop.is_mappable"
                                   @change="handleSelectionChange(index, prop, 'crm', 'default')">
                          <el-option-group
                              v-for="group in hubspotPropertiesOptions"
                              :key="group.label"
                              :label="group.label"
                          >
                            <el-option
                                v-for="option in group.data"
                                :key="option.name"
                                :label="option.label"
                                :value="option.name"
                                :disabled="!option.is_mappable || isSelectedOption(option.name, prop.external_key, integration)">
                              <i v-if="isSelectedOption(option.name, prop.external_key, integration)"
                                 class="fa fa-ban text-grey-5">
                              </i>
                              {{ option.direction === IntegrationPropertyMapDirections.DIRECTION_INBOUND ? `${option.label} (${option.field_type}, read-only)` : `${option.label} (${option.field_type})` }}
                            </el-option>
                          </el-option-group>
                        </el-select>
                      </el-form-item>
                    </div>
                    <!-- End HubSpot Fields -->

                    <!-- Start Delete button -->
                    <div v-if="prop.is_mappable"
                         class="col-md-2 text-center">
                      <el-button size="small"
                                 circle
                                 plain
                                 :disabled="deleteMappingDisabled(prop.aloware_key)"
                                 @click="removeDefaultPropertyMapping(index)">
                        <i class="fa fa-trash"></i>
                      </el-button>
                    </div>
                    <div v-else class="col-md-2 text-center mt-2">
                      <i class="fa fa-lock fa-lg text-grey-4"></i>
                    </div>
                    <!-- End Delete Button -->
                  </div>
                </div>
                <!-- Start Add mapping button -->
                <div class="row form-group">
                    <div class="col-md-10 text-center">
                        <el-button size="small"
                                   @click="addDefaultPropertyMapping">
                            <i class="fa fa-plus-circle"></i>
                            <span class="ml-1 font-weight-bold">ADD MAPPING</span>
                        </el-button>
                    </div>
                </div>
                <!-- End Add mapping button -->
                <!-- End Default Fields -->

                <!-- Start Custom Fields -->
              <template v-if="integration === 'hubspot'">
                <h5>Custom Fields</h5>
                <div class="row form-group mt-3"
                     v-for="(prop, index) in customPropertyMappings"
                     :key="`${index}-custom`"
                >
                    <!-- Start Aloware and Integration Labels -->
                    <template v-if="index === 0">
                        <div class="col-md-4 mb-1">
                            <small class="font-weight-bold">Aloware</small>
                        </div>
                        <div class="col-md-2 mb-1 text-center">
                            <small class="font-weight-bold">Direction</small>
                        </div>
                        <div class="col-md-4 mb-1">
                            <small class="font-weight-bold"> {{ getIntegrationTitleName() }} </small>
                        </div>
                    </template>
                    <!-- End Aloware and HubSpot Labels -->

                    <!-- Start Aloware Field -->
                    <div class="col-md-4">
                        <el-form-item class="mb-1">
                            <el-select v-model="prop.aloware_key"
                                       class="w-full"
                                       filterable
                                       :disabled="!prop.is_mappable"
                                        @change="handleSelectionChange(index, prop, 'aloware', 'custom')">
                                <el-option
                                    v-for="option in alowareCustomOptions"
                                    :key="option['key']"
                                    :label="option['label']"
                                    :disabled="!option['is_mappable'] || isSelectedOption(option['key'], prop.aloware_key)"
                                    :value="option['key']">
                                    <i v-if="isSelectedOption(option['key'], prop.aloware_key)"
                                       class="fa fa-ban text-grey-5">
                                    </i>
                                    {{ option['label'] }}
                                </el-option>
                            </el-select>
                        </el-form-item>
                    </div>
                    <!-- End Aloware Fields -->

                    <div class="col-md-2 text-center">
                        <direction-selector :initial-state="prop.direction"
                                            :disabled="!prop.is_mappable || shouldDisableDirection(index, prop, 'custom')"
                                            @change="direction => updateCustomDirection(index, direction)">
                        </direction-selector>
                    </div>

                    <!-- Start HubSpot Fields -->
                    <div class="col-md-4">
                        <el-form-item class="mb-1">
                            <el-select v-model="prop.external_key"
                                       class="w-full"
                                       filterable
                                       :disabled="!prop.is_mappable"
                                       @change="handleSelectionChange(index, prop, 'crm', 'custom')">
                                <el-option-group
                                    v-for="group in hubspotPropertiesOptions"
                                    :key="group.label"
                                    :label="group.label"
                                >
                                    <el-option
                                        v-for="option in group.data"
                                        :key="option.name"
                                        :label="option.label"
                                        :value="option.name"
                                        :disabled="!option.is_mappable || isSelectedOption(option.name, prop.external_key, integration)"
                                        @change="handleSelectionChange(index, prop, 'crm', 'custom')">
                                        <i v-if="isSelectedOption(option.name, prop.external_key, integration)"
                                           class="fa fa-ban text-grey-5">
                                        </i>
                                      {{ option.direction === IntegrationPropertyMapDirections.DIRECTION_INBOUND ? `${option.label} (${option.field_type}, read-only)` : `${option.label} (${option.field_type})` }}
                                    </el-option>
                                </el-option-group>
                            </el-select>
                        </el-form-item>
                    </div>
                    <!-- End HubSpot Fields -->

                    <!-- Start Delete button -->
                    <div v-if="prop.is_mappable"
                         class="col-md-2 text-center">
                        <el-button size="small"
                                   circle
                                   plain
                                   @click="removeCustomPropertyMapping(index)">
                            <i class="fa fa-trash"></i>
                        </el-button>
                    </div>
                    <div v-else class="col-md-2 text-center mt-2">
                        <i class="fa fa-lock fa-lg text-grey-4"></i>
                    </div>
                    <!-- End Delete Button -->
                </div>
                <!-- Start Add mapping button -->
                <div class="row form-group">
                    <div class="col-md-10 text-center">
                        <el-button size="small"
                                   @click="addCustomPropertyMapping">
                            <i class="fa fa-plus-circle"></i>
                            <span class="ml-1 font-weight-bold">ADD MAPPING</span>
                        </el-button>
                    </div>
                </div>
                <!-- End Add mapping button -->
              </template>
                <!-- End Custom Properties Fields -->
            </el-form>
        </div>
    </el-card>
</template>

<script>
import {mapActions, mapGetters, mapMutations} from 'vuex'
import {handleVuexApiCall, sleep} from '../../../utils/integration-helpers'
import DirectionSelector from "../direction-selector.vue"
import * as IntegrationPropertyMapDirections from '../../../constants/integration-property-map-directions'
import {MODULE_CONTACT, MODULE_LEAD} from "../../../../constants/integration_modules";

export default {
    name: 'integration-property-mapper',
    components: {DirectionSelector},
    props: {
      integration: {
        type: String,
        required: true,
      },
      module: {
        type: String,
        default: MODULE_CONTACT
      },
      defaultPropertyMappings: { type: Array, default: () => [] },
      customPropertyMappings: { type: Array, default: () => [] },
    },
    data: () => ({
        // ui
        isLoading: false,

        // mappings
        IntegrationPropertyMapDirections
    }),
    computed: {
        ...mapGetters('hubspotPropertyMapping', ['getPropertyMappings', 'getCrmContactProperties', 'getAlowareContactProperties']),
        alowareDefaultOptions() {
            return this.$store.getters['hubspotPropertyMapping/getAlowareContactProperties']['default']
        },
        alowareCustomOptions() {
            return this.$store.getters['hubspotPropertyMapping/getAlowareContactProperties']['custom']
        },
         crmPropertiesOptions() {
            return this.$store.getters['hubspotPropertyMapping/getCrmContactProperties']
        },
        hubspotPropertiesOptions() {
            const newOptions = []
            const current_module_key = this.getSourceModuleNumber(this.module)

            for (const prop of this.$store.getters['hubspotPropertyMapping/getCrmContactProperties']) {
                if (prop?.module_section && prop.module_section !== current_module_key) {
                  continue
                }

                let groupExists = false

                for (const newOption of newOptions) {
                    if (newOption.label === prop.group_label) {
                        newOption.data.push(prop)
                        groupExists = true
                    }
                }

                if (!groupExists) newOptions.push({label: prop.group_label, data: [prop]})
            }

            return newOptions
        }
    },
    methods: {
        getSourceModuleNumber (module) {
          switch (module) {
            case MODULE_LEAD:
              return 2
            case MODULE_CONTACT:
            default:
              return 1
          }
        },
        isCorrectModule (module) {
          switch (this.module) {
            case MODULE_LEAD:
              return module === this.getSourceModuleNumber(MODULE_LEAD)
            case MODULE_CONTACT:
            default:
              return module === this.getSourceModuleNumber(MODULE_CONTACT)
          }
        },
        addDefaultPropertyMapping() {
            this.defaultPropertyMappings.push({
                aloware_key: '',
                external_key: '',
                is_mappable: true,
                is_default: true,
                direction: IntegrationPropertyMapDirections.DIRECTION_BI,
                source_module: this.getSourceModuleNumber(this.module)
            })
        },
        removeDefaultPropertyMapping(index) {
            this.defaultPropertyMappings.splice(index, 1)
        },
        handleSelectionChange(index, prop, type = 'aloware', field = 'default') {
            if (!['aloware', 'crm'].includes(type)) {
                throw new Error("type must either be 'aloware' or 'crm'")
            }

            if (!['default', 'custom'].includes(field)) {
                throw new Error("field must either be 'default' or 'custom'")
            }

            // HubSpot integration needs to handle INBOUND-only direction from the CRM for both default and custom mappings
            if (this.integration === 'hubspot') {
                return this.handleHubspotSelectionChange(index, prop, type, field)
            }

            // Handle other integrations. They do not care for selection changes from the side of the CRM
            // or the custom mappings
            if (type === 'crm' || field === 'custom') return

            const selectedOption = this.alowareDefaultOptions.find(option => option.key === prop.aloware_key)

            if (!selectedOption) {
                return
            }

            if (selectedOption.direction !== prop.direction) {
                this.updateDefaultDirection(index, selectedOption.direction)
            }
        },
        /**
         * HubSpot integration needs to handle OUTBOUND-only direction from Aloware and INBOUND-only direction from the CRM
         *
         * Conditions:
         * 1. Aloware and HubSpot properties are both BI-DIRECTIONAL = No conflict
         * 2. Aloware and HubSpot properties are both INBOUND-ONLY = No conflict
         * 3. Aloware and HubSpot properties are both OUTBOUND-ONLY = No conflict
         * 4. Aloware property is BI-DIRECTIONAL and HubSpot property is INBOUND-ONLY = Direction will be forced to INBOUND
         * 5. Aloware property is INBOUND-ONLY and HubSpot property is BI-DIRECTIONAL = Direction will be forced to INBOUND
         * 6. Aloware property is OUTBOUND-ONLY and HubSpot property is BI-DIRECTIONAL = Direction will be forced to OUTBOUND
         * 7. Aloware property is BI-DIRECTIONAL and HubSpot property is OUTBOUND-ONLY = Direction will be forced to OUTBOUND
         * 8. Aloware property is INBOUND-ONLY and HubSpot property is OUTBOUND-ONLY = Conflict, will not save the mappings
         * 9. Aloware property is OUTBOUND-ONLY and HubSpot property is INBOUND-ONLY = Conflict, will not save the mappings
         */
        handleHubspotSelectionChange(index, prop, type, field) {
            let selectedOption

            // Get the selected option from the Aloware or CRM (default or custom)
            if (type === 'aloware') {
                selectedOption = field === 'default'
                    ? this.alowareDefaultOptions.find(option => option.key === prop.aloware_key)
                    : this.alowareCustomOptions.find(option => option.key === prop.aloware_key)
            } else {
                selectedOption = this.crmPropertiesOptions.find(option => option.name === prop.external_key)
            }

            // Get the current mapping from the default or custom mappings
            let currentMapping = this.defaultPropertyMappings[index]

            // Check if there is a DIRECTION conflict
            if (this.hubspotHasDirectionConflict(index, prop, type, field, selectedOption, currentMapping)) {
                // If there is a conflict, clear the selected option from the mapping
                if (type === 'aloware') currentMapping.aloware_key = ''
                else currentMapping.external_key = ''

                // Notify the user that there is a conflict
                this.$notify({
                    title: 'Contact Property Mapping',
                    message: 'Direction conflict detected. Please select a different property.',
                    type: 'error',
                    showClose: true
                })
            }
        },
        /**
          * Check if there is a DIRECTION conflict.
          * E.g. An OUTBOUND only aloware property cannot be mapped to an INBOUND only HubSpot property.
          */
        hubspotHasDirectionConflict(index, prop, type, field, selectedOption, currentMapping) {
            let counterpartOption = null
            if (type === 'aloware') counterpartOption = this.crmPropertiesOptions.find(option => option.name === currentMapping.external_key)
            else {
                counterpartOption = field === 'default'
                    ? this.alowareDefaultOptions.find(option => option.key === currentMapping.aloware_key)
                    : this.alowareCustomOptions.find(option => option.key === currentMapping.aloware_key)
            }

            const updateDirection = field === 'default' ? this.updateDefaultDirection : this.updateCustomDirection
            if (!counterpartOption) {
                if (selectedOption.direction !== prop.direction) {
                    updateDirection(index, selectedOption.direction)
                }

                return false
            }

            // Selected option and counterpart option are the same direction = No conflict
            if (selectedOption.direction === counterpartOption.direction) {
                return false
            }

            // Selected option is BI-DIRECTIONAL and counterpart option is INBOUND-ONLY = INBOUND-ONLY will be forced
            if (selectedOption.direction === IntegrationPropertyMapDirections.DIRECTION_BI && counterpartOption.direction === IntegrationPropertyMapDirections.DIRECTION_INBOUND) {
                updateDirection(index, IntegrationPropertyMapDirections.DIRECTION_INBOUND)
                return false
            }

            // Selected option is INBOUND-ONLY and counterpart option is BI-DIRECTIONAL = INBOUND-ONLY will be forced
            if (selectedOption.direction === IntegrationPropertyMapDirections.DIRECTION_INBOUND && counterpartOption.direction === IntegrationPropertyMapDirections.DIRECTION_BI) {
                updateDirection(index, IntegrationPropertyMapDirections.DIRECTION_INBOUND)
                return false
            }

            // Selected option is OUTBOUND-ONLY and counterpart option is BI-DIRECTIONAL = OUTBOUND-ONLY will be forced
            if (selectedOption.direction === IntegrationPropertyMapDirections.DIRECTION_OUTBOUND && counterpartOption.direction === IntegrationPropertyMapDirections.DIRECTION_BI) {
                updateDirection(index, IntegrationPropertyMapDirections.DIRECTION_OUTBOUND)
                return false
            }

            // Selected option is BI-DIRECTIONAL and counterpart option is OUTBOUND-ONLY = Conflict, will not save the mappings
            if (selectedOption.direction === IntegrationPropertyMapDirections.DIRECTION_BI && counterpartOption.direction === IntegrationPropertyMapDirections.DIRECTION_OUTBOUND) {
                updateDirection(index, IntegrationPropertyMapDirections.DIRECTION_OUTBOUND)
                return false
            }

            // Selected option is INBOUND-ONLY and counterpart option is OUTBOUND-ONLY = Conflict, will not save the mappings
            if (selectedOption.direction === IntegrationPropertyMapDirections.DIRECTION_INBOUND && counterpartOption.direction === IntegrationPropertyMapDirections.DIRECTION_OUTBOUND) {
              return true
            }

            // Selected option is OUTBOUND-ONLY and counterpart option is INBOUND-ONLY = Conflict, will not save the mappings
            if (selectedOption.direction === IntegrationPropertyMapDirections.DIRECTION_OUTBOUND && counterpartOption.direction === IntegrationPropertyMapDirections.DIRECTION_INBOUND) {
                return true
            }

            return false
        },
        shouldDisableDirection(index, prop, field = 'default') {
            if (!['default', 'custom'].includes(field)) {
                throw new Error("field must either be 'default' or 'custom'")
            }

            if (this.integration === 'hubspot') return this.hubspotShouldDisableDirection(prop, field)

            // Handle other integrations. They do not care for selection changes from the side of the CRM
            const selectedOption = this.alowareDefaultOptions.find(option => option.key === prop.aloware_key)
            const isNotBiDirectional = selectedOption?.direction !== IntegrationPropertyMapDirections.DIRECTION_BI

            return !prop.is_mappable || isNotBiDirectional
        },
        /**
         * HubSpot integration needs to also handle OUTBOUND-only direction from the Aloware
         * and INBOUND-only direction from the CRM for both default and custom mappings
         */
        hubspotShouldDisableDirection(prop, field) {
            // Get the selected option from the Aloware or CRM (default or custom)
            const alowareSelectedOption = field === 'default'
                ? this.alowareDefaultOptions.find(option => option.key === prop.aloware_key)
                : this.alowareCustomOptions.find(option => option.key === prop.aloware_key)

            const alowareSelectionNotBiDirectional = alowareSelectedOption?.direction !== IntegrationPropertyMapDirections.DIRECTION_BI

            const crmSelectedOption = this.crmPropertiesOptions.find(option => option.name === prop.external_key)
            const crmSelectionNotBiDirectional = crmSelectedOption?.direction !== IntegrationPropertyMapDirections.DIRECTION_BI
            const isNotBiDirectional = crmSelectionNotBiDirectional || alowareSelectionNotBiDirectional

            return !prop.is_mappable || isNotBiDirectional
        },
        addCustomPropertyMapping() {
            this.customPropertyMappings.push({
                aloware_key: '',
                external_key: '',
                is_mappable: true,
                is_default: false,
                direction: IntegrationPropertyMapDirections.DIRECTION_BI,
                source_module: this.getSourceModuleNumber(this.module),
            })
        },
        removeCustomPropertyMapping(index) {
            this.customPropertyMappings.splice(index, 1)
        },
        isSelectedOption(option, selectedOption, propOwner = 'aloware') {
            if (!['aloware', this.integration].includes(propOwner)) throw new Error(`propOwner must either be 'aloware' or ${this.integration}`)

            const mergedMappings = this.defaultPropertyMappings.concat(this.customPropertyMappings)
            const keyType = propOwner === 'aloware' ? 'aloware_key' : 'external_key'
            const current_module_number = this.getSourceModuleNumber(this.module)
            const optionKeys = mergedMappings.filter(filter => filter['source_module'] === current_module_number)
                .map(mapping => mapping[keyType])

            return optionKeys.includes(option) && option !== selectedOption
        },
        updateDefaultDirection(index, direction) {
            this.defaultPropertyMappings[index].direction = direction
        },

        updateCustomDirection(index, direction) {
            this.customPropertyMappings[index].direction = direction
        },

        getIntegrationTitleName() {
            return this.integration.toLowerCase().replace(/\b\w/g, s => s.toUpperCase())
        },
      
        alowareKeySelectionIsLocked(alowareKey) {
            return alowareKey === 'hubspot_owner'
        },
      
        deleteMappingDisabled(alowareKey) {
            return alowareKey === 'hubspot_owner'
        },
      
        hasAllowedOpenCustomDirections(alowareKey) {
            const keysWithForcedOpenDirections = ['hubspot_lead_status']
            if (!keysWithForcedOpenDirections.includes(alowareKey)) {
              return false
            }
            
            return true
        },
      
        allowedOpenCustomDirections(alowareKey) {
            // Just the contact disposition for now
            const keysWithForcedOpenDirections = ['hubspot_lead_status']
            if (!keysWithForcedOpenDirections.includes(alowareKey)) {
              return []
            }
            
            // We only have an INBOUND and BI-directional custom directions for now
            return [
              IntegrationPropertyMapDirections.DIRECTION_INBOUND,
              IntegrationPropertyMapDirections.DIRECTION_BI,
            ]
        }
    }
}
</script>
